import { useState, useMemo } from 'react';
import { Plus, TrendingUp, TrendingDown, Coins, ArrowDownUp } from 'lucide-react';
import KpiCard from '../components/KpiCard.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import PettyCashFormModal from '../components/PettyCashFormModal.jsx';
import PettyCashBalanceChart from '../components/PettyCashBalanceChart.jsx';
import PettyCashCategoryBreakdown from '../components/PettyCashCategoryBreakdown.jsx';
import PettyCashMovementCard from '../components/PettyCashMovementCard.jsx';
import { usePettyCashMovements, usePettyCashSummary } from '../hooks/usePettyCash.js';
import { formatCurrency } from '../lib/format.js';

// ── Helpers de période ────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { label: '30j', days: 30 },
  { label: '3 mois', days: 90 },
  { label: '6 mois', days: 180 },
  { label: '1 an', days: 365 },
];

function offsetDate(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const TODAY = new Date().toISOString().slice(0, 10);

// ── Section collapse wrapper ──────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PettyCashPage() {
  const [periodDays, setPeriodDays] = useState(30);
  const [modalState, setModalState] = useState({ open: false, mode: 'create', movement: null });

  const from = useMemo(() => offsetDate(periodDays), [periodDays]);
  const to = TODAY;

  const {
    data: movementsData,
    isLoading: movementsLoading,
    error: movementsError,
  } = usePettyCashMovements({ from, to, limit: 200 });

  const {
    data: summary,
    isLoading: summaryLoading,
  } = usePettyCashSummary(from, to);

  const movements = Array.isArray(movementsData)
    ? movementsData
    : movementsData?.movements ?? movementsData?.data ?? [];

  const hasOpening = movements.some((m) => m.kind === 'opening');

  const balanceCents = summary?.balance_cents ?? 0;
  const totalIn = summary?.total_in_cents ?? 0;
  const totalOut = summary?.total_out_cents ?? 0;
  const byCategory = summary?.by_category ?? [];
  const balanceSeries = summary?.balance_series ?? [];

  const balancePositive = balanceCents >= 0;

  function openCreate() {
    setModalState({ open: true, mode: 'create', movement: null });
  }

  function openOpening() {
    setModalState({ open: true, mode: 'opening', movement: null });
  }

  function openEdit(movement) {
    setModalState({ open: true, mode: 'edit', movement });
  }

  function closeModal() {
    setModalState((s) => ({ ...s, open: false }));
  }

  const isLoading = movementsLoading || summaryLoading;

  return (
    <div className="relative space-y-5 pb-24 lg:pb-6">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Caisse</h1>
          <p className="text-sm text-slate-500">Mouvements cash physiques</p>
        </div>

        {/* Bouton desktop */}
        <button
          type="button"
          onClick={openCreate}
          className="hidden lg:flex items-center gap-1.5 bg-slate-800 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors shrink-0"
          aria-label="Créer un nouveau mouvement de caisse"
        >
          <Plus size={15} aria-hidden />
          Nouveau mouvement
        </button>
      </div>

      {/* ── Filtre période ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" role="group" aria-label="Période d'analyse">
        {PERIOD_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => setPeriodDays(opt.days)}
            className={`shrink-0 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
              periodDays === opt.days
                ? 'bg-slate-800 text-white border-slate-800'
                : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* ── Erreur ─────────────────────────────────────────────────────────── */}
      {movementsError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger les données : {movementsError.message}
        </div>
      )}

      {/* ── Chargement ─────────────────────────────────────────────────────── */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && (
        <>
          {/* ── État vide : pas d'opening ──────────────────────────────────── */}
          {!hasOpening && movements.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                <Coins size={18} className="text-slate-500" aria-hidden />
              </div>
              <h2 className="text-base font-semibold text-slate-900 mb-1">
                Caisse non initialisée
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Commencez par définir le solde d'ouverture de votre caisse.
              </p>
              <button
                type="button"
                onClick={openOpening}
                className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Plus size={14} aria-hidden />
                Définir le solde d'ouverture
              </button>
            </div>
          )}

          {/* ── Solde + KPIs ────────────────────────────────────────────────── */}
          {(hasOpening || movements.length > 0) && (
            <>
              {/* Solde courant */}
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-4 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">
                  Solde actuel
                </p>
                <p
                  className={`text-4xl font-bold tabular-nums ${
                    balancePositive ? 'text-green-700' : 'text-red-600'
                  }`}
                  aria-label={`Solde : ${formatCurrency(balanceCents)}`}
                >
                  {formatCurrency(balanceCents)}
                </p>
              </div>

              {/* KPI entrées/sorties */}
              <div className="grid grid-cols-2 gap-3">
                <KpiCard
                  label="Entrées"
                  value={formatCurrency(totalIn)}
                  icon={TrendingUp}
                  accentClass="text-green-700"
                />
                <KpiCard
                  label="Sorties"
                  value={formatCurrency(totalOut)}
                  icon={TrendingDown}
                  accentClass="text-red-600"
                />
              </div>

              {/* Graphique évolution solde */}
              {balanceSeries.length > 0 && (
                <Section title="Évolution du solde">
                  <PettyCashBalanceChart series={balanceSeries} />
                </Section>
              )}

              {/* Répartition par catégorie */}
              {byCategory.length > 0 && (
                <Section title="Répartition par catégorie">
                  <PettyCashCategoryBreakdown byCategory={byCategory} />
                </Section>
              )}

              {/* Journal */}
              <Section title="Journal">
                {movements.length === 0 ? (
                  <div className="py-6 text-center text-sm text-slate-400">
                    Aucun mouvement sur cette période
                  </div>
                ) : (
                  <>
                    {/* Mobile : cards */}
                    <div className="space-y-2 md:hidden">
                      {movements.map((m) => (
                        <PettyCashMovementCard
                          key={m.id}
                          movement={m}
                          onEdit={openEdit}
                        />
                      ))}
                    </div>

                    {/* Desktop : table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="py-2 pl-4 pr-2 text-xs font-medium text-slate-500 uppercase tracking-wide">Sens</th>
                            <th className="py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wide">Date</th>
                            <th className="py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wide">Catégorie</th>
                            <th className="py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wide">Commentaire</th>
                            <th className="py-2 px-2 text-xs font-medium text-slate-500 uppercase tracking-wide">Commande</th>
                            <th className="py-2 px-2 pr-4 text-xs font-medium text-slate-500 uppercase tracking-wide text-right">Montant</th>
                            <th className="py-2 pl-2 pr-4 text-xs font-medium text-slate-500 uppercase tracking-wide text-right">
                              <ArrowDownUp size={12} aria-hidden />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {movements.map((m) => (
                            <PettyCashMovementCard
                              key={m.id}
                              movement={m}
                              onEdit={openEdit}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Section>
            </>
          )}
        </>
      )}

      {/* ── FAB mobile ──────────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={openCreate}
        className="fixed bottom-20 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-slate-800 text-white shadow-lg hover:bg-slate-700 active:scale-95 transition-all lg:hidden"
        aria-label="Nouveau mouvement de caisse"
      >
        <Plus size={22} aria-hidden />
      </button>

      {/* ── Modal ────────────────────────────────────────────────────────────── */}
      <PettyCashFormModal
        open={modalState.open}
        onClose={closeModal}
        mode={modalState.mode}
        movement={modalState.movement}
      />
    </div>
  );
}
